import React, { useMemo, useState } from "react";

import { filterByField, filterBySearch, sortRows } from "@/utils/listDisplay.js";

import { useKarateUi } from "@/hooks/useKarateUi.js";



/**

 * Tableau compact avec recherche, tri et filtre (GSD listes/tableaux).

 * @param {object} props

 * @param {Record<string, unknown>[]} props.rows

 * @param {{ key: string, label: string, sortable?: boolean }[]} props.columns

 * @param {string[]} props.searchFields

 * @param {{ field: string, label: string, options: { value: string, label: string }[] }[]} [props.filters]

 * @param {string} [props.emptyMessage]
 * @param {string} [props.emptyFilteredMessage]

 * @param {string} [props.testId]

 */

export function DataTable({

  rows,

  columns,

  searchFields,

  filters = [],

  emptyMessage,

  emptyFilteredMessage,

  testId = "data-table",

}) {

  const { ui } = useKarateUi();

  const [search, setSearch] = useState("");

  const [sortKey, setSortKey] = useState(columns[0]?.key ?? null);

  const [sortDir, setSortDir] = useState(/** @type {'asc'|'desc'} */ ("asc"));

  const [filterValues, setFilterValues] = useState({});



  const displayed = useMemo(() => {

    let result = rows;

    result = filterBySearch(result, search, searchFields);

    for (const f of filters) {

      result = filterByField(result, f.field, filterValues[f.field] ?? "all");

    }

    return sortRows(result, sortKey, sortDir);

  }, [rows, search, searchFields, filters, filterValues, sortKey, sortDir]);



  const toggleSort = (key) => {

    if (sortKey === key) {

      setSortDir((d) => (d === "asc" ? "desc" : "asc"));

    } else {

      setSortKey(key);

      setSortDir("asc");

    }

  };



  const resetFilters = () => {

    setSearch("");

    setFilterValues({});

  };



  const hasActiveFilters =

    search.trim().length > 0 || Object.values(filterValues).some((v) => v && v !== "all");

  const resolvedEmpty = hasActiveFilters
    ? (emptyFilteredMessage ?? ui.table.emptyFiltered)
    : (emptyMessage ?? ui.table.empty);



  return (

    <div className="data-table-wrap" data-testid={testId}>

      <div className="data-table-toolbar">

        <input

          type="search"

          placeholder={ui.table.searchPlaceholder}

          value={search}

          onChange={(e) => setSearch(e.target.value)}

          data-testid={`${testId}-search`}

          aria-label={ui.table.searchAria}

        />

        {filters.map((f) => (

          <select

            key={f.field}

            value={filterValues[f.field] ?? "all"}

            onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.field]: e.target.value }))}

            data-testid={`${testId}-filter-${f.field}`}

            aria-label={f.label}

          >

            <option value="all">{f.label} — {ui.table.filterAllSuffix}</option>

            {f.options.map((o) => (

              <option key={o.value} value={o.value}>

                {o.label}

              </option>

            ))}

          </select>

        ))}

        {hasActiveFilters ? (

          <button type="button" className="btn-secondary" onClick={resetFilters} data-testid={`${testId}-reset`}>

            {ui.table.reset}

          </button>

        ) : null}

      </div>

      <div className="table-scroll">

        <table className="data-table">

          <thead>

            <tr>

              {columns.map((col) => (

                <th key={col.key}>

                  {col.sortable !== false ? (

                    <button

                      type="button"

                      className="th-sort"

                      onClick={() => toggleSort(col.key)}

                      data-testid={`${testId}-sort-${col.key}`}

                    >

                      {col.label}

                      {sortKey === col.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}

                    </button>

                  ) : (

                    col.label

                  )}

                </th>

              ))}

            </tr>

          </thead>

          <tbody>

            {displayed.length === 0 ? (

              <tr>

                <td colSpan={columns.length} className="empty-cell" data-testid={`${testId}-empty`}>

                  {resolvedEmpty}

                </td>

              </tr>

            ) : (

              displayed.map((row, idx) => (

                <tr key={String(row.id ?? idx)} data-testid={`${testId}-row-${row.id ?? idx}`}>

                  {columns.map((col) => (

                    <td key={col.key}>{col.render ? col.render(row) : String(row[col.key] ?? "")}</td>

                  ))}

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      <p className="table-count" data-testid={`${testId}-count`}>

        {ui.table.count(displayed.length, rows.length)}

      </p>

    </div>

  );

}


